import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * GitHub-style "type this phrase to confirm" modal.
 *
 * Confirm button stays disabled until the user types the exact phrase
 * (case-insensitive, trimmed). Cancel + ESC + backdrop click dismiss.
 */
export default function ConfirmTypeModal({
  open,
  title,
  description,
  phrase,
  confirmLabel = "Confirm",
  danger = false,
  requireFile = false,
  fileAccept = "image/*,application/pdf",
  fileLabel = "Supporting screenshot (image or PDF)",
  onConfirm,
  onCancel,
}) {
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open) {
      setInput("");
      setFile(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onCancel]);

  if (!open) return null;
  const matches = input.trim().toLowerCase() === phrase.toLowerCase();
  const fileOk = !requireFile || !!file;

  const handleConfirm = () => {
    if (requireFile) onConfirm(file);
    else onConfirm();
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3>{title}</h3>
        {description && <p className="muted" style={{ marginTop: 8 }}>{description}</p>}
        <div style={{ marginTop: 18 }}>
          <label className="field">
            To confirm, type <strong style={{ color: "var(--ink)" }}>{phrase}</strong>
          </label>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        {requireFile && (
          <div style={{ marginTop: 16 }}>
            <label className="field">{fileLabel}</label>
            <input
              type="file"
              accept={fileAccept}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file && (
              <div className="muted-sm" style={{ marginTop: 6 }}>
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>
        )}
        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className={danger ? "primary danger-action" : "primary"}
            disabled={!matches || !fileOk}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
